/**
 * Editor SEO Panel Component
 * SEO settings for the page
 */
import React, { useState } from "react";
import { Box, Button as MuiButton, IconButton, Typography, Paper, TextField } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

interface SEOPanelProps {
  seo: { title?: string; description?: string; keywords?: string };
  pageName: string;
  onSEOChange: (seo: { title?: string; description?: string; keywords?: string }) => void;
  onClose: () => void;
}

export const EditorSEOPanel: React.FC<SEOPanelProps> = ({ seo, pageName, onSEOChange, onClose }) => {
  const [localSEO, setLocalSEO] = useState({
    title: seo.title || "",
    description: seo.description || "",
    keywords: seo.keywords || "",
  });

  const handleSave = () => {
    onSEOChange(localSEO);
    onClose();
  };

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        bgcolor: "white",
        zIndex: 20,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          p: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "grey.50",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="subtitle2" fontWeight={500}>
          SEO Settings
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Page Title */}
        <Box>
          <Typography variant="body2" fontWeight={500} gutterBottom>
            Page Title
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={localSEO.title}
            onChange={(e) => setLocalSEO({ ...localSEO, title: e.target.value })}
            placeholder={pageName}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
            {localSEO.title.length}/60 characters
          </Typography>
        </Box>

        {/* Meta Description */}
        <Box>
          <Typography variant="body2" fontWeight={500} gutterBottom>
            Meta Description
          </Typography>
          <TextField
            size="small"
            fullWidth
            multiline
            rows={3}
            value={localSEO.description}
            onChange={(e) => setLocalSEO({ ...localSEO, description: e.target.value })}
            placeholder="Brief description of this page for search engines..."
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
            {localSEO.description.length}/160 characters
          </Typography>
        </Box>

        {/* Keywords */}
        <Box>
          <Typography variant="body2" fontWeight={500} gutterBottom>
            Keywords
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={localSEO.keywords}
            onChange={(e) => setLocalSEO({ ...localSEO, keywords: e.target.value })}
            placeholder="keyword1, keyword2, keyword3"
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
            Separate keywords with commas
          </Typography>
        </Box>

        {/* Preview */}
        <Paper elevation={0} sx={{ p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
          <Typography variant="body2" fontWeight={500} gutterBottom>
            Google Preview
          </Typography>
          <Paper elevation={0} sx={{ p: 1.5, bgcolor: "white", border: 1, borderColor: "divider" }}>
            <Typography
              sx={{
                color: "#1a0dab",
                fontSize: 18,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {localSEO.title || pageName}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#006621", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              https://yoursite.com/{pageName.toLowerCase().replace(/\s+/g, "-")}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {localSEO.description || "No description set. Add a meta description to improve SEO."}
            </Typography>
          </Paper>
        </Paper>
      </Box>

      {/* Save Button */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: "divider", bgcolor: "grey.50" }}>
        <MuiButton variant="contained" fullWidth onClick={handleSave}>
          Save SEO Settings
        </MuiButton>
      </Box>
    </Box>
  );
};

export default EditorSEOPanel;
